// Reports Center: the pipeline, source split, counsellor comparison and weekly inflow
// charts, then a searchable table of the same students. Charts and table share one
// filtered list, so a number in a chart always matches the rows below it.
import { useRef, useState } from 'react';

import { CsvPanel, useCsvExport } from 'src/front-components/lead-desk/csv-export';
import { BarChart, DataTable, Donut, donutColor, FilterBar, LineChart, MultiSelect, Pager, type Column } from 'src/front-components/lead-desk/kit';
import { StatusCell } from 'src/front-components/lead-desk/screens-home';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { PageMasthead, ShellCard, ShellEmptyState, ShellGrid, ShellSectionLabel } from 'src/front-components/theme/page-chrome';
import { EditDrawer } from 'src/front-components/lead-desk/student-drawers';
import { IconButton, UpdatedBadge } from 'src/front-components/lead-desk/kit';
import { DeskButton, DeskInput, DeskSelect } from 'src/front-components/theme/desk-controls';
import { getPalette } from 'src/front-components/theme/ewings-theme';
import { getShellColors, SHELL_SPACE, SHELL_TYPE, v } from 'src/front-components/theme/shell-tokens';
import {
  counsellorLoad,
  customRangeError,
  DATE_RANGES,
  isRecentlyUpdated,
  toggleStatusFilter,
  type CustomRange,
  DESK_STATUS_LABELS,
  filterStudents,
  formatCallDate,
  inDateRange,
  NO_FILTERS,
  paginate,
  pipelineCounts,
  SOURCE_LABELS,
  sourceBreakdown,
  sortStudents,
  studentPhone,
  studentsToCsv,
  UNASSIGNED,
  weeklyInflow,
  type DateRange,
  type StudentRow,
} from 'src/utils/lead-desk';

const PAGE_SIZE = 15;

export const ReportsScreen = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  const [range, setRange] = useState<DateRange>('ALL');
  const [custom, setCustom] = useState<CustomRange>({ from: '', to: '' });
  const [editing, setEditing] = useState<StudentRow | null>(null);
  const [counsellors, setCounsellors] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(1);
  const exporter = useCsvExport();
  const tableRef = useRef<HTMLDivElement | null>(null);

  const scrollToTable = () => {
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };
  const locked = ctx.profile.scope !== 'ALL';

  const rangeError = range === 'CUSTOM' ? customRangeError(custom) : null;
  const ranged = rangeError === null ? inDateRange(ctx.students, range, new Date(), custom) : [];
  const scoped = filterStudents(ranged, { ...NO_FILTERS, counsellors: locked ? [] : counsellors });
  const pipeline = pipelineCounts(scoped).filter((p) => p.count > 0 || ['NEW', 'CONTACTED', 'INTERESTED', 'ENROLLED', 'NOT_ENROLLED'].includes(p.key));
  const sources = sourceBreakdown(scoped);
  const load = counsellorLoad(scoped);
  const inflow = weeklyInflow(scoped);

  const table = sortStudents(
    scoped.filter((s) => {
      const q = query.trim().toLowerCase();

      if (status !== '' && s.deskStatus !== status) return false;
      if (source !== '' && s.leadSource !== source) return false;

      return q === '' || [s.name, studentPhone(s), s.counsellorName].some((x) => (x ?? '').toLowerCase().includes(q));
    }),
  );
  const paged = paginate(table, page, PAGE_SIZE);

  const columns: Column<StudentRow>[] = [
    {
      key: 'name',
      header: 'STUDENT NAME',
      width: '1.4fr',
      render: (s) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: SHELL_SPACE[1], flexWrap: 'wrap' }}>
          <span style={{ ...SHELL_TYPE.bodyMedium }}>{s.name ?? '—'}</span>
          
        </span>
      ),
    },
    { key: 'mobile', header: 'MOBILE NUMBER', width: '130px', render: (s) => studentPhone(s) },
    { key: 'counsellor', header: 'COUNSELOR', width: '140px', render: (s) => s.counsellorName ?? UNASSIGNED },
    { key: 'telecaller', header: 'TELECALLER', width: '140px', render: (s) => s.telecallerName ?? UNASSIGNED },
    { key: 'next', header: 'NEXT FOLLOW-UP', width: '140px', render: (s) => (s.nextCallAt ? formatCallDate(s.nextCallAt) : '—') },
    { key: 'source', header: 'LEAD SOURCE', width: '130px', render: (s) => SOURCE_LABELS[s.leadSource ?? ''] ?? '—' },
    { key: 'status', header: 'CURRENT STATUS', width: '210px', render: (s) => <StatusCell student={s} /> },
    ...(ctx.profile.canEditStudent
      ? [{ key: 'edit', header: 'ACTIONS', width: '70px', align: 'right' as const, render: (s: StudentRow) => <IconButton scheme={ctx.scheme} icon="edit" tone="green" label={`Edit ${s.name ?? 'student'}`} onClick={() => setEditing(s)} /> }]
      : []),
  ];

  return (
    <>
      <PageMasthead
        palette={getPalette(ctx.scheme)}
        scheme={ctx.scheme}
        title="Admissions Reports & Analytics"
        subtitle="Interactive charts showing pipeline conversion and counselor workloads"
      />

      <FilterBar scheme={ctx.scheme}>
        <span style={{ ...SHELL_TYPE.label, color: shell.textSecondary }}>Date Range:</span>
        <DeskSelect scheme={ctx.scheme} width="150px" value={range} onChange={(value) => { setRange(value as DateRange); setPage(1); }} options={DATE_RANGES} />
        {range === 'CUSTOM' && (
          <>
            <DeskInput scheme={ctx.scheme} type="date" value={custom.from} onChange={(from) => { setCustom({ ...custom, from }); setPage(1); }} width="150px" />
            <span style={{ ...SHELL_TYPE.body, color: shell.textSecondary }}>to</span>
            <DeskInput scheme={ctx.scheme} type="date" value={custom.to} onChange={(to) => { setCustom({ ...custom, to }); setPage(1); }} width="150px" />
            {rangeError !== null && <span style={{ ...SHELL_TYPE.caption, color: v('color-red') }}>{rangeError}</span>}
          </>
        )}
        <span style={{ ...SHELL_TYPE.label, color: shell.textSecondary }}>Counselor:</span>
        <MultiSelect
          scheme={ctx.scheme}
          label="All counselors"
          width="220px"
          options={ctx.counsellors.map((c) => ({ value: c, label: c }))}
          selected={counsellors}
          onChange={(next) => { setCounsellors(next); setPage(1); }}
          locked={locked ? ctx.person || ctx.profile.label : undefined}
        />
        <span style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>Filters apply automatically</span>
      </FilterBar>

      <ShellGrid min={280}>
        <ShellCard scheme={ctx.scheme}>
          <ShellSectionLabel scheme={ctx.scheme}>Admissions Lead Pipeline</ShellSectionLabel>
          <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginBottom: SHELL_SPACE[2] }}>Students at each stage of the admissions cycle.</div>
          {scoped.length === 0 ? <ShellEmptyState scheme={ctx.scheme} message="No students in this range." /> : <BarChart
              scheme={ctx.scheme}
              data={pipeline.map((p) => ({ label: p.label, value: p.count, key: p.key }))}
              activeKey={status}
              onBarClick={(key) => {
                setStatus(toggleStatusFilter(status, key));
                setPage(1);
                scrollToTable();
              }}
            />}
        </ShellCard>

        <ShellCard scheme={ctx.scheme}>
          <ShellSectionLabel scheme={ctx.scheme}>Lead Source Conversion</ShellSectionLabel>
          <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginBottom: SHELL_SPACE[2] }}>Share of admission registration channels.</div>
          {sources.length === 0 ? (
            <ShellEmptyState scheme={ctx.scheme} message="No students in this range." />
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: SHELL_SPACE[3], maxWidth: '100%' }}>
              <Donut
                scheme={ctx.scheme}
                parts={sources.slice(0, 8).map((s) => ({ label: s.label, value: s.count, key: s.key }))}
                centre={scoped.length}
                caption="ACTIVE LEADS"
                activeKey={source}
                onPartClick={(key) => {
                  setSource(source === key ? '' : key);
                  setPage(1);
                  scrollToTable();
                }}
              />
              <div style={{ flex: 1 }}>
                {sources.slice(0, 8).map((s, index) => (
                  <div
                    key={s.key}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSource(source === s.key ? '' : s.key);
                      setPage(1);
                      scrollToTable();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: SHELL_SPACE[2],
                      ...SHELL_TYPE.body,
                      color: shell.text,
                      padding: `${SHELL_SPACE.half} ${SHELL_SPACE[1]}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      background: source === s.key ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
                      fontWeight: source === s.key ? 600 : 400,
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: donutColor(index), flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{s.label}</span>
                    <span style={{ color: shell.textSecondary }}>{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ShellCard>

        <ShellCard scheme={ctx.scheme}>
          <ShellSectionLabel scheme={ctx.scheme}>Counselor Performance Comparison</ShellSectionLabel>
          <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginBottom: SHELL_SPACE[2] }}>Leads assigned vs enrolled students, by counselor.</div>
          {load.length === 0 ? (
            <ShellEmptyState scheme={ctx.scheme} message="No students in this range." />
          ) : (
            <BarChart
              scheme={ctx.scheme}
              data={load.slice(0, 8).flatMap((l) => [
                { label: l.name, value: l.assigned, color: v('color-blue'), key: l.name },
                { label: `${l.name.split(' ')[0]} ✓`, value: l.enrolled, color: v('color-green'), key: l.name },
              ])}
              onBarClick={(name) => {
                setQuery(name);
                setPage(1);
                scrollToTable();
              }}
            />
          )}
        </ShellCard>

        <ShellCard scheme={ctx.scheme}>
          <ShellSectionLabel scheme={ctx.scheme}>Weekly Inflow Trend</ShellSectionLabel>
          <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginBottom: SHELL_SPACE[2] }}>New student profiles by weekday.</div>
          <LineChart scheme={ctx.scheme} data={inflow.map((d) => ({ label: d.day, value: d.count }))} />
        </ShellCard>
      </ShellGrid>

      <div style={{ height: SHELL_SPACE[4] }} />
      <CsvPanel scheme={ctx.scheme} panel={exporter.panel} onClose={exporter.close} />
      <div ref={tableRef} style={{ scrollMarginTop: 80 }}>
        <ShellCard scheme={ctx.scheme}>
        <ShellSectionLabel
          scheme={ctx.scheme}
          action={
            ctx.profile.canExport ? (
              <DeskButton scheme={ctx.scheme} onClick={() => exporter.run('student-records.csv', studentsToCsv(table))}>
                Export
              </DeskButton>
            ) : undefined
          }
        >
          Student Records Details
        </ShellSectionLabel>
        <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, marginBottom: SHELL_SPACE[3] }}>Showing {table.length} records.</div>

        <FilterBar scheme={ctx.scheme}>
          <DeskInput scheme={ctx.scheme} value={query} onChange={(value) => { setQuery(value); setPage(1); }} placeholder="Search (Name/Mobile/Counselor)" width="320px" />
          <DeskSelect
            scheme={ctx.scheme}
            width="190px"
            value={status}
            onChange={(value) => { setStatus(value); setPage(1); }}
            options={[{ value: '', label: 'All Statuses' }, ...Object.entries(DESK_STATUS_LABELS).map(([value, label]) => ({ value, label }))]}
          />
          <DeskSelect
            scheme={ctx.scheme}
            width="190px"
            value={source}
            onChange={(value) => { setSource(value); setPage(1); }}
            options={[{ value: '', label: 'All Sources' }, ...Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label }))]}
          />
        </FilterBar>

        <DataTable scheme={ctx.scheme} columns={columns} rows={paged.items} rowKey={(s) => s.id} empty="No students match these filters." dense />
        <Pager scheme={ctx.scheme} page={paged.page} pages={paged.pages} onPage={setPage} summary={`Showing ${paged.items.length} of ${table.length} records`} />
      </ShellCard>
      </div>

      {editing !== null && <EditDrawer ctx={ctx} student={editing} onClose={() => setEditing(null)} />}
    </>
  );
};
